import React from "react";
import * as Dropdown from "../../../../../common-deprecated/styles/lexus/Dropdown";
import Text from "../../../../../common-deprecated/styles/lexus/Text";
import { useCompareV2Selector } from "../../../redux/store";
import type { CarDataType } from "../../../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import { getEcoSpecs } from "../../../utils/formatters/priceAndEcoSpecsFormatters";
import CompareEta from "../../common/highlight/content/CompareEta";
import * as CompareEntryStyles from "../styles/CompareEntry";
import SelectEngine from "./content/SelectEngine";
import Rating from "./content/Rating";
import PriceSpec from "./content/PriceSpec";
import { loadEtaValuesInCompare, hideReviewScoresInCompare } from "../../../utils/settings";
import { CompareV2Label } from "../../../utils/labels";
import Icon from "../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import Popover from "../../../../../common-deprecated/components/popover/aem/lexus/Popover";

type CompareSpecsPropsType = {
    car: CarDataType;
    index: number;
    engineSelectHeight: number;
    setEngineSelectHeight: (height: number) => void;
    isPdf?: boolean;
};

const HighlightedSpecs = (props: CompareSpecsPropsType): JSX.Element => {
    const { car, index, engineSelectHeight, setEngineSelectHeight, isPdf } = props;
    const selectedEngine = useCompareV2Selector((state) => state.newCarCompare.selectedEngines[index]);
    const specs = useCompareV2Selector((state) => state.newCarCompare.parsedSpecs);
    const carHasMultipleEngines = useCompareV2Selector((state) => state.newCarCompare.specs[index].length > 1);
    const showPrice = useCompareV2Selector((state) => state.commonSettings.showPrice);
    const compareV2Settings = useCompareV2Selector((state) => state.compareV2Settings);
    const commonSettings = useCompareV2Selector((state) => state.commonSettings);

    const showEta = loadEtaValuesInCompare(compareV2Settings);
    const hideReviews = hideReviewScoresInCompare(compareV2Settings, commonSettings);
    const ecoSpecs = getEcoSpecs(specs, compareV2Settings.labels);

    return (
        <>
            <CompareEntryStyles.Wrapper>
                <CompareEntryStyles.Title>
                    <CompareV2Label label="compareBodyTopEngine" />
                </CompareEntryStyles.Title>
                <Dropdown.Wrapper isLoading={car.loading} onlyOneOption={!carHasMultipleEngines}>
                    <SelectEngine
                        isPdf={isPdf}
                        index={index}
                        engineSelectHeight={engineSelectHeight}
                        setEngineSelectHeight={setEngineSelectHeight}
                    />
                </Dropdown.Wrapper>
            </CompareEntryStyles.Wrapper>

            {showPrice && <PriceSpec car={car} motorization={selectedEngine} />}

            {ecoSpecs.map((spec) => (
                <CompareEntryStyles.Wrapper key={spec.titles.join("-")}>
                    <CompareEntryStyles.Title>
                        {spec.titles[index] || spec.titles[0]}
                        {spec.tooltip && <Popover showOnClick content={spec.tooltip} useHTML />}
                    </CompareEntryStyles.Title>
                    <CompareEntryStyles.KeyValue>
                        <CompareEntryStyles.Value isLoading={car.loading}>
                            {spec.values[index] === "" && <Icon variant="minus" />}
                            {spec.values[index]}
                        </CompareEntryStyles.Value>
                        {!car.loading && spec.values[index] && spec.unit && (
                            <CompareEntryStyles.Suffix>
                                {typeof spec.unit !== "string" ? spec.unit[index] : spec.unit}
                            </CompareEntryStyles.Suffix>
                        )}
                    </CompareEntryStyles.KeyValue>
                </CompareEntryStyles.Wrapper>
            ))}

            {showEta && (
                <CompareEntryStyles.Wrapper>
                    <CompareEntryStyles.Value>
                        <Text pt={{ xs: 1, md: 0 }} display={{ xs: "block", md: "inline" }}>
                            <CompareEta
                                date={car.eta && car.eta.date}
                                etaLoading={car.eta && car.eta.loading}
                                error={!car.eta || !!car.eta.errorMessage}
                            />
                        </Text>
                    </CompareEntryStyles.Value>
                </CompareEntryStyles.Wrapper>
            )}

            {!hideReviews && !!car.ownerRating && car.ownerRating >= 0 && (
                <CompareEntryStyles.Wrapper>
                    <CompareEntryStyles.Title>
                        <CompareV2Label label="compareBodyOwnerReviews" />
                    </CompareEntryStyles.Title>
                    <CompareEntryStyles.KeyValue>
                        <CompareEntryStyles.Value>
                            <Rating score={car.ownerRating} />
                        </CompareEntryStyles.Value>
                    </CompareEntryStyles.KeyValue>
                </CompareEntryStyles.Wrapper>
            )}
        </>
    );
};

export default HighlightedSpecs;
